export const TYPE_VALUE_KEYS = {
  STRING: 'STRING',
  NUMBER: 'NUMBER',
  BOOLEAN: 'BOOLEAN',
  ARRAY: 'ARRAY',
  UNDEFINED: 'UNDEFINED',
  OBJECT: 'OBJECT',
  NULL: 'NULL',
  CUSTOM: 'CUSTOM'
}
export const TYPE_VALUES_VALUE = {
  STRING: '',
  NUMBER: 0,
  BOOLEAN: false,
  ARRAY: [],
  UNDEFINED: undefined,
  OBJECT: {},
  NULL: null,
  CUSTOM: () => {}
}

type ValueType =
  | 'STRING'
  | 'NUMBER'
  | 'BOOLEAN'
  | 'ARRAY'
  | 'UNDEFINED'
  | 'OBJECT'
  | 'NULL'
  | 'CUSTOM' // replace ... with other possible values

export const getPropertyByPath = <T = any>(
  object: Record<string, any> | null, // replace any with the actual type if known
  key: string | string[],
  valueType?: ValueType,
  valueDefault?: ValueType extends 'CUSTOM' ? () => T : T
): T | undefined => {
  // replace any with the actual return type if known
  let keys: string[] = []

  if (typeof key === 'string') keys = key.split('.')

  if (Array.isArray(key)) keys = [...key]

  let value: any = object // replace any with the actual type if known

  for (let i = 0; i < keys.length; i++) {
    const currentKey = keys[i]

    if (value !== null && typeof value === 'object' && currentKey in value) {
      value = value[currentKey]
    } else {
      if (valueType !== undefined) {
        if (valueDefault !== null && valueType === 'CUSTOM' && typeof valueDefault === 'function')
          return valueDefault() as T
        return valueType === 'CUSTOM'
          ? (valueDefault as T)
          : (TYPE_VALUES_VALUE[valueType] as T | undefined)
      }
      return undefined
    }
  }

  // If the value is a boolean, return it directly
  if (typeof value === 'boolean') {
    return value as T
  }

  return value as T
}

export const omit = (obj: Record<string, any>, arr: string[]) => {
  if (!obj) return {}
  if (!arr) return obj

  return Object.keys(obj)
    .filter((k) => !arr.includes(k))
    .reduce<Record<string, any>>((acc, key) => {
      acc[key] = obj[key]
      return acc
    }, {})
}

/*!
 * Add items to an object at a specific path
 * (c) 2021 Chris Ferdinandi, MIT License, https://gomakethings.com
 * @param  {Object}       obj  The object
 * @param  {String|Array} path The path to assign the value to
 * @param  {*}            val  The value to assign
 */
export function setValueToObjectByPath(
  obj: Record<string, any>,
  path: string | string[],
  val: unknown
) {
  const object = structuredClone(obj)
  /**
   * If the path is a string, convert it to an array
   * @param  {String|Array} path The path
   * @return {Array}             The path array
   */
  function stringToPath(path: string | string[]): string[] {
    // If the path isn't a string, return it
    if (typeof path !== 'string') return path

    // Create new array
    const output: string[] = []

    // Split to an array with dot notation
    path.split('.').forEach((item) => {
      // Split to an array with bracket notation
      item.split(/\[([^}]+)\]/g).forEach((key) => {
        // Push to the new array
        if (key.length > 0) {
          output.push(key)
        }
      })
    })

    return output
  }

  // Convert the path to an array if not already
  const pathArray = stringToPath(path)

  // Cache the path length and current spot in the object
  const length = pathArray.length
  let current = object

  // Loop through the path
  pathArray.forEach((key: string, index: number) => {
    // Check if the assigned key should be an array
    const isArray = key.slice(-2) === '[]'

    // If so, get the true key name by removing the trailing []
    key = isArray ? key.slice(0, -2) : key

    // If the key should be an array and isn't, create an array
    if (isArray && !Array.isArray(current[key])) {
      current[key] = []
    }

    // If this is the last item in the loop, assign the value
    if (index === length - 1) {
      // If it's an array, push the value
      // Otherwise, assign it
      if (isArray) {
        current[key].push(val)
      } else {
        current[key] = val
      }
      // Otherwise, update the current place in the object
    } else {
      // If the key doesn't exist, create it
      if (!current[key]) {
        current[key] = {}
      }

      // Update the current place in the object
      current = current[key]
    }
  })

  // Return the object
  return object
}
